import { useState, useEffect, useRef, useCallback } from 'react';

// Singleton flag to prevent duplicate CSS injection
let starRatingStylesInjected = false;

interface StarRatingProps {
  total?: number;
  delay?: number;
}

export default function StarRating({ total = 5, delay = 0 }: StarRatingProps) {
  const [filled, setFilled] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Use ref for the guard so trigger identity stays stable
  const startedRef = useRef(false);

  const trigger = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarted(true);
    let count = 0;
    intervalRef.current = setInterval(() => {
      count += 1;
      setFilled(count);
      if (count >= total) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 130);
  }, [total]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timeoutRef.current = setTimeout(trigger, delay);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [trigger, delay]);

  // Inject star animation CSS once globally (singleton pattern)
  useEffect(() => {
    if (starRatingStylesInjected) return;
    starRatingStylesInjected = true;

    const style = document.createElement('style');
    style.setAttribute('data-star-rating', '');
    style.textContent = `
      @keyframes starPop {
        0%   { opacity: 0; transform: scale(0) rotate(-45deg); filter: drop-shadow(0 0 0px transparent); }
        50%  { opacity: 1; transform: scale(1.55) rotate(10deg); filter: drop-shadow(0 0 10px rgba(251,191,36,0.9)); }
        75%  { transform: scale(0.82) rotate(-4deg); filter: drop-shadow(0 0 5px rgba(251,191,36,0.6)); }
        90%  { transform: scale(1.12) rotate(2deg); }
        100% { opacity: 1; transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 3px rgba(251,191,36,0.35)); }
      }
      @keyframes starPulse {
        0%, 100% { transform: scale(1); filter: drop-shadow(0 0 3px rgba(251,191,36,0.35)); }
        30%  { transform: scale(1.12); filter: drop-shadow(0 0 7px rgba(251,191,36,0.6)); }
        60%  { transform: scale(1.03); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const el = document.querySelector('style[data-star-rating]');
      if (el) el.remove();
      starRatingStylesInjected = false;
    };
  }, []);

  return (
    <div ref={ref} className="flex items-center gap-1">
      {[...Array(total)].map((_, i) => {
        const isFilled = i < filled;
        return (
          <span
            key={i}
            className="relative inline-flex w-[14px] h-[14px] items-center justify-center"
          >
            <i className="ri-star-line text-amber-400 text-sm absolute inset-0 flex items-center justify-center"></i>
            {isFilled && (
              <i
                className="ri-star-fill text-amber-400 text-sm absolute inset-0 flex items-center justify-center"
                style={{
                  animation: `starPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, starPulse 2s ${1.2 + i * 0.35}s ease-in-out infinite`,
                }}
              ></i>
            )}
          </span>
        );
      })}
    </div>
  );
}